import React from 'react';
import {action, computed, makeObservable, observable} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';

// Admin-only positioner for the old-style engraved-plate overlay (EPOverlay). Mounted
// from product.tsx in both EP admin modes - ?ep_admin (product with plate templates) and
// EP admin mode ep_type=6, which the backend template page links into - on old/none-style
// products (CustomizationMethodID < 2). The arrows nudge the plate by 0.01" per click and
// the inputs accept typed values; Zoom scales the plate on the old-style photo (1 = actual
// size, 0.95 = 95%). A Plate 1 / Plate 2 selector (shown when the template has a second
// plate) switches the card between the plate-1 fields (ep_xpos / ep_ypos / ep_zoom) and
// the plate-2 fields (ep2_xpos / ep2_ypos / ep2_zoom). Copy the final X/Y/Zoom (and the
// checkbox) into the backend plate template's "Plate Template Line Settings" card (xpos /
// ypos / zoom / show_oldstyle in Entities.settingsJSON; the second plate's values go into
// the "Second plate" X Pos / Y Pos / Zoom of the same card, stored as settingsJSON.plate2)
// to persist them.
const STEP = 0.01;

const round2 = (v: number): number => Math.round(v * 100) / 100;

const arrowStyle: React.CSSProperties = {width: 34, height: 30};

type PlateIndex = 1 | 2;

@observer
export default class EPPositionAdmin extends React.Component {
    @observable plate: PlateIndex = 1;
    // The inputs bind these string drafts, not the store numbers, so an in-progress
    // entry like "-" or an emptied field isn't clobbered back to "0" mid-keystroke
    // (negative coordinates are half the space: +X right, +Y down from center).
    @observable draftX = String(s.ps.ep_xpos ?? 0);
    @observable draftY = String(s.ps.ep_ypos ?? 0);
    @observable draftZoom = String(s.ps.ep_zoom > 0 ? s.ps.ep_zoom : 1);

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @computed get hasPlate2(): boolean {
        return s.ps.EP2Width > 0 && s.ps.EP2Height > 0;
    }

    private get xKey() {
        return this.plate === 2 ? 'ep2_xpos' : 'ep_xpos';
    }

    private get yKey() {
        return this.plate === 2 ? 'ep2_ypos' : 'ep_ypos';
    }

    private get zoomKey() {
        return this.plate === 2 ? 'ep2_zoom' : 'ep_zoom';
    }

    private redraw() {
        s.ui.doRender = true;
        s.pulse();
    }

    @action
    selectPlate = (plate: PlateIndex) => {
        this.plate = plate;
        this.draftX = String(s.ps[this.xKey] ?? 0);
        this.draftY = String(s.ps[this.yKey] ?? 0);
        this.draftZoom = String(s.ps[this.zoomKey] > 0 ? s.ps[this.zoomKey] : 1);
    };

    @action
    nudge = (dx: number, dy: number) => {
        // Only touch the nudged axis so a typed higher-precision value on the other
        // axis isn't silently re-rounded.
        if (dx !== 0) {
            s.ps[this.xKey] = round2((s.ps[this.xKey] || 0) + dx);
            this.draftX = String(s.ps[this.xKey]);
        }
        if (dy !== 0) {
            s.ps[this.yKey] = round2((s.ps[this.yKey] || 0) + dy);
            this.draftY = String(s.ps[this.yKey]);
        }
        this.redraw();
    };

    @action
    onXChange = (raw: string) => {
        this.draftX = raw;
        const v = parseFloat(raw);
        if (isFinite(v)) {
            s.ps[this.xKey] = v;
            this.redraw();
        }
    };

    @action
    onYChange = (raw: string) => {
        this.draftY = raw;
        const v = parseFloat(raw);
        if (isFinite(v)) {
            s.ps[this.yKey] = v;
            this.redraw();
        }
    };

    @action
    onZoomChange = (raw: string) => {
        this.draftZoom = raw;
        const v = parseFloat(raw);
        if (isFinite(v) && v > 0) {
            s.ps[this.zoomKey] = v;
            this.redraw();
        }
    };

    @action
    toggleShow = (checked: boolean) => {
        s.ps.ep_show_oldstyle = checked;
        // The overlay only renders while a plate is attached; auto-add one (like ep_add)
        // so enabling the preview always shows something to position. prerender's
        // EngravedPlateStatusID==0 reset exempts ep_show_oldstyle so this survives.
        if (checked && (!s.ps.ep_id || s.ps.ep_id <= 0)) {
            s.ps.ep_id = s.ps.ep_id_view || 37;
        }
        this.redraw();
    };

    render() {
        const plateLabel = this.plate === 2 ? 'Second plate' : 'Plate';

        return (
            <div
                className="gray-box"
                role="region"
                aria-label="Engraved plate old-style position admin"
            >
                <h2 style={{fontSize: 18, margin: '0 0 8px 0'}}>Plate Position (Old Style)</h2>
                <p style={{fontSize: 12, lineHeight: 1.4, margin: '0 0 8px 0'}}>
                    Inches from the frame center (+X right, +Y down). Zoom scales the plate (1 = actual size, 0.95 = 95%). Save the values in the backend plate template settings (X Pos / Y Pos / Zoom / Show on old style; the second plate's values go in the "Second plate" X Pos / Y Pos / Zoom of the
                    same card).
                </p>
                <label style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8}}>
                    <input
                        type="checkbox"
                        checked={!!s.ps.ep_show_oldstyle}
                        onChange={e => this.toggleShow(e.target.checked)}
                    />
                    Show plate on old style
                </label>
                {this.hasPlate2 && (
                    <div
                        role="radiogroup"
                        aria-label="Which plate to position"
                        style={{display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8}}
                    >
                        {([1, 2] as PlateIndex[]).map(p => (
                            <label
                                key={p}
                                style={{display: 'flex', alignItems: 'center', gap: 4, margin: 0}}
                            >
                                <input
                                    type="radio"
                                    name="ep-position-admin-plate"
                                    checked={this.plate === p}
                                    onChange={() => this.selectPlate(p)}
                                />
                                {p === 1 ? `Plate 1 (${s.ps.EPWidth}" x ${s.ps.EPHeight}")` : `Plate 2 (${s.ps.EP2Width}" x ${s.ps.EP2Height}")`}
                            </label>
                        ))}
                    </div>
                )}
                <div style={{display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap'}}>
                    <label style={{display: 'flex', alignItems: 'center', gap: 4}}>
                        X
                        <input
                            type="number"
                            step={STEP}
                            className="form-control ib input-qty"
                            style={{width: 90}}
                            value={this.draftX}
                            onChange={e => this.onXChange(e.target.value)}
                            aria-label={`${plateLabel} X position in inches`}
                        />
                    </label>
                    <label style={{display: 'flex', alignItems: 'center', gap: 4}}>
                        Y
                        <input
                            type="number"
                            step={STEP}
                            className="form-control ib input-qty"
                            style={{width: 90}}
                            value={this.draftY}
                            onChange={e => this.onYChange(e.target.value)}
                            aria-label={`${plateLabel} Y position in inches`}
                        />
                    </label>
                    <label style={{display: 'flex', alignItems: 'center', gap: 4}}>
                        Zoom
                        <input
                            type="number"
                            step={STEP}
                            min={0.1}
                            className="form-control ib input-qty"
                            style={{width: 90}}
                            value={this.draftZoom}
                            onChange={e => this.onZoomChange(e.target.value)}
                            aria-label={`${plateLabel} zoom factor (1 = actual size)`}
                        />
                    </label>
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 34px)', gap: 2, justifyItems: 'center'}}>
                        <span />
                        <button
                            type="button"
                            style={arrowStyle}
                            onClick={() => this.nudge(0, -STEP)}
                            aria-label={`Move ${plateLabel.toLowerCase()} up 0.01 inches`}
                        >
                            ▲
                        </button>
                        <span />
                        <button
                            type="button"
                            style={arrowStyle}
                            onClick={() => this.nudge(-STEP, 0)}
                            aria-label={`Move ${plateLabel.toLowerCase()} left 0.01 inches`}
                        >
                            ◀
                        </button>
                        <span />
                        <button
                            type="button"
                            style={arrowStyle}
                            onClick={() => this.nudge(STEP, 0)}
                            aria-label={`Move ${plateLabel.toLowerCase()} right 0.01 inches`}
                        >
                            ▶
                        </button>
                        <span />
                        <button
                            type="button"
                            style={arrowStyle}
                            onClick={() => this.nudge(0, STEP)}
                            aria-label={`Move ${plateLabel.toLowerCase()} down 0.01 inches`}
                        >
                            ▼
                        </button>
                        <span />
                    </div>
                </div>
            </div>
        );
    }
}
